import { useEffect, useState, useRef, useCallback, useMemo } from "react";
import styled from "styled-components";

// ── Constants ────────────────────────────────────────────────────────────────

const ROW_HEIGHT = 36;
const COL_MIN_WIDTH = 120;
const OVERSCAN = 15;

// ── Styled Components ────────────────────────────────────────────────────────

const FileInfo = styled.div`
  margin-bottom: 12px;
  padding: 10px 16px;
  background: ${({ $isSaved }) => ($isSaved ? "#dbeeff" : "#ffdecd")};
  border: 1px solid ${({ $isSaved }) => ($isSaved ? "#bfdbfe" : "#ff5b45")};
  border-radius: 8px;
  color: ${({ $isSaved }) => ($isSaved ? "#003594" : "#931313")};
  font-weight: 600;
  font-size: 13px;
  display: flex;
  justify-content: space-between;
  align-items: center;

  span {
    margin-left: 8px;
    font-weight: 400;
  }

  button {
    border: none;
    border-radius: 6px;
    padding: 6px 16px;
    background-color: #003594;
    color: #fff;
    font-weight: 700;
    font-size: 12px;
    cursor: pointer;
    transition: background 0.2s;
    &:hover { background-color: #00205b; }
  }
`;

const ModelHeading = styled.div`
  font-size: 11px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.1em;
  color: #003594;
  margin-bottom: 6px;
`;

const TableWrapper = styled.div`
  display: flex;
  flex-direction: column;
  height: clamp(300px, 70vh, 80vh);
  border: 2px solid #c8c9c7;
  border-radius: 8px;
  overflow: hidden;
  background: #fff;
`;

const HeaderScroll = styled.div`
  overflow: hidden;
  flex-shrink: 0;
`;

const HeaderRow = styled.div`
  display: flex;
  background-color: #003594;
`;

const HeaderCell = styled.div`
  flex-shrink: 0;
  padding: 10px 14px;
  text-align: left;
  font-size: 11px;
  font-weight: 600;
  color: #fff;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  border-right: 1px solid rgba(255, 255, 255, 0.15);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  gap: 4px;

  input {
    padding: 2px 6px;
    font-size: 10px;
    width: 100%;
    box-sizing: border-box;
    border-radius: 3px;
    border: 1px solid rgba(255, 255, 255, 0.3);
    background: rgba(255, 255, 255, 0.88);
    color: #000;
    outline: none;
  }

  &:last-child { border-right: none; }
`;

const BodyScroll = styled.div`
  flex: 1;
  min-height: 0;
  overflow: auto;
`;

const VirtualRow = styled.div`
  display: flex;
  position: absolute;
  border-bottom: 1px solid #e5e7eb;
  box-sizing: border-box;
  cursor: default;
  background-color: ${({ $checked, $even }) =>
    $checked ? "#fff8e1" : $even ? "#fafbfc" : "#fff"};

  &:hover {
    background-color: ${({ $checked }) => ($checked ? "#ffd699" : "#dbeeff")};
  }
`;

const Cell = styled.div`
  flex-shrink: 0;
  padding: 6px 12px;
  font-size: 12px;
  color: #00205b;
  border-right: 1px solid #c8c9c7;
  line-height: 1.4;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  box-sizing: border-box;
  display: flex;
  align-items: center;

  input[type="checkbox"] {
    width: 14px;
    height: 14px;
    accent-color: #003594;
    cursor: pointer;
  }

  &:last-child { border-right: none; }
`;

const Toolbar = styled.div`
  padding: 6px 10px;
  flex-shrink: 0;
  display: flex;
  align-items: center;
  gap: 10px;
  border-bottom: 1px solid #c8c9c7;
  background: #fafbfc;
  font-size: 12px;
  color: #555;
`;

const ClearBtn = styled.button`
  padding: 3px 10px;
  font-size: 11px;
  border: 1px solid #c8c9c7;
  border-radius: 4px;
  background: #fff;
  cursor: pointer;
  color: #00205b;
  &:hover { background: #dbeeff; }
`;

const EmptyState = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 60px 20px;
  width: 100%;
`;

const EmptyIcon = styled.div`
  font-size: 44px;
  margin-bottom: 14px;
  opacity: 0.4;
`;

const EmptyTitle = styled.div`
  font-size: 15px;
  font-weight: 700;
  color: #475569;
  margin-bottom: 6px;
`;

const EmptySubtitle = styled.div`
  font-size: 13px;
  color: #94a3b8;
`;

// ── Helpers ──────────────────────────────────────────────────────────────────

function applyConstOff(row, negIdx, posIdx, initialIdxs) {
  return row.map((cell, j) => {
    if (j === negIdx || j === posIdx) return "";
    if (initialIdxs.includes(j)) return "0";
    return cell;
  });
}

function applyConstOn(row, negIdx, posIdx, initialIdxs) {
  return row.map((cell, j) => {
    if (j === negIdx || j === posIdx) return "";
    if (initialIdxs.includes(j)) return "1";
    return cell;
  });
}

// ── Component ────────────────────────────────────────────────────────────────

export default function ExcelTable({
  data,
  headers,
  fileName,
  tbData,
  setTbData,
  handleSaveButton,
}) {
  const [checkedRows, setCheckedRows] = useState(new Set());
  const [constStatus, setConstStatus] = useState({});
  const [scrollTop, setScrollTop] = useState(0);
  const [containerHeight, setContainerHeight] = useState(400);
  const [filterText, setFilterText] = useState({});

  const bodyRef = useRef(null);
  const headerScrollRef = useRef(null);
  const resizeObserverRef = useRef(null);
  const filterTimerRef = useRef(null);

  useEffect(() => {
    setTbData(data ?? []);
    setCheckedRows(new Set());
    setConstStatus({});
    setFilterText({});
    setScrollTop(0);
  }, [data]);

  // Measure container height
  useEffect(() => {
    const el = bodyRef.current;
    if (!el) return;
    const measure = () => setContainerHeight(el.clientHeight);
    measure();
    if (typeof ResizeObserver !== "undefined") {
      resizeObserverRef.current = new ResizeObserver(measure);
      resizeObserverRef.current.observe(el);
    }
    return () => resizeObserverRef.current?.disconnect();
  }, [tbData]);

  const handleBodyScroll = useCallback((e) => {
    setScrollTop(e.currentTarget.scrollTop);
    if (headerScrollRef.current)
      headerScrollRef.current.scrollLeft = e.currentTarget.scrollLeft;
  }, []);

  const handleFilterChange = useCallback((header, value) => {
    if (filterTimerRef.current) clearTimeout(filterTimerRef.current);
    filterTimerRef.current = setTimeout(() => {
      setFilterText((prev) => ({ ...prev, [header]: value }));
      setScrollTop(0);
      if (bodyRef.current) bodyRef.current.scrollTop = 0;
    }, 200);
  }, []);

  const clearFilters = useCallback(() => {
    setFilterText({});
    setScrollTop(0);
    if (bodyRef.current) bodyRef.current.scrollTop = 0;
  }, []);

  if (!data?.length || !headers) {
    return (
      <EmptyState>
        <EmptyIcon>📊</EmptyIcon>
        <EmptyTitle>No data to display</EmptyTitle>
        <EmptySubtitle>Upload an Excel file to see the table</EmptySubtitle>
      </EmptyState>
    );
  }

  const posIdx = headers.indexOf("Positive Regulation Rule");
  const negIdx = headers.indexOf("Negative Regulation Rule");
  const initialIdxs = headers
    .map((h, i) => (h.toLowerCase().includes("state list") ? i : -1))
    .filter((i) => i !== -1);

  // Filter rows
  const filteredData = (tbData ?? []).filter((row) =>
    Object.keys(filterText).every((key) => {
      const fv = (filterText[key] || "").trim().toLowerCase();
      if (!fv) return true;
      const colIdx = headers.indexOf(key);
      return String(row[colIdx] ?? "").toLowerCase().includes(fv);
    }),
  );

  // Build a map from filtered index → original index
  const filteredIndexMap = new Map();
  let fi = 0;
  (tbData ?? []).forEach((row, origIdx) => {
    if (filteredData.includes(row)) {
      filteredIndexMap.set(fi, origIdx);
      fi++;
    }
  });

  const colWidths = headers.map((h) => Math.max(COL_MIN_WIDTH, h.length * 8 + 32));
  const totalWidth = colWidths.reduce((s, w) => s + w, 0);
  const totalHeight = filteredData.length * ROW_HEIGHT;
  const hasFilter = Object.values(filterText).some((v) => v);

  const startIndex = Math.max(0, Math.floor(scrollTop / ROW_HEIGHT) - OVERSCAN);
  const visibleCount = Math.ceil(containerHeight / ROW_HEIGHT) + 2 * OVERSCAN;
  const endIndex = Math.min(filteredData.length, startIndex + visibleCount);

  const resetRow = (rowIndex) =>
    setTbData((prev) =>
      prev.map((r, i) => (i === rowIndex ? [...data[rowIndex]] : r)),
    );

  const uncheck = (rowIndex) => {
    resetRow(rowIndex);
    setConstStatus((prev) => ({ ...prev, [rowIndex]: null }));
    setCheckedRows((prev) => { const n = new Set(prev); n.delete(rowIndex); return n; });
  };

  const check = (rowIndex, status, applyFn) => {
    resetRow(rowIndex);
    setTbData((prev) =>
      prev.map((r, i) => i === rowIndex ? applyFn(r, negIdx, posIdx, initialIdxs) : r),
    );
    setConstStatus((prev) => ({ ...prev, [rowIndex]: status }));
    setCheckedRows((prev) => new Set(prev).add(rowIndex));
  };

  const handleConstOff = (e, rowIndex) =>
    e.target.checked ? check(rowIndex, "OFF", applyConstOff) : uncheck(rowIndex);
  const handleConstOn = (e, rowIndex) =>
    e.target.checked ? check(rowIndex, "ON", applyConstOn) : uncheck(rowIndex);

  const hasEdits = checkedRows.size > 0;

  const visibleRows = [];
  for (let i = startIndex; i < endIndex; i++) {
    const row = filteredData[i];
    const originalIndex = filteredIndexMap.get(i) ?? i;
    const isChecked = checkedRows.has(originalIndex);

    visibleRows.push(
      <VirtualRow
        key={originalIndex}
        $checked={isChecked}
        $even={i % 2 === 1}
        style={{ top: i * ROW_HEIGHT, height: ROW_HEIGHT, width: totalWidth }}
      >
        {headers.map((header, colIdx) => {
          const h = (header || "").trim();
          const val = row[colIdx] != null && row[colIdx] !== "" ? String(row[colIdx]) : "";
          return (
            <Cell key={colIdx} style={{ width: colWidths[colIdx], minWidth: colWidths[colIdx] }} title={val}>
              {h === "Const OFF" ? (
                <input
                  type="checkbox"
                  checked={constStatus[originalIndex] === "OFF"}
                  onChange={(e) => handleConstOff(e, originalIndex)}
                />
              ) : h === "Const ON" ? (
                <input
                  type="checkbox"
                  checked={constStatus[originalIndex] === "ON"}
                  onChange={(e) => handleConstOn(e, originalIndex)}
                />
              ) : (
                val
              )}
            </Cell>
          );
        })}
      </VirtualRow>
    );
  }

  return (
    <div>
      {fileName && (
        <>
          <ModelHeading>Model</ModelHeading>
          <FileInfo $isSaved={!hasEdits}>
            <div>
              📊 {fileName}
              <span>({data.length} rows)</span>
            </div>
            {hasEdits && <button onClick={handleSaveButton}>Save Changes</button>}
          </FileInfo>
        </>
      )}

      <TableWrapper>
        <Toolbar>
          {hasFilter && (
            <ClearBtn onClick={clearFilters}>Clear Filters</ClearBtn>
          )}
          <span>
            Showing {filteredData.length} of {(tbData ?? []).length} rows
          </span>
        </Toolbar>

        {/* Header */}
        <HeaderScroll ref={headerScrollRef}>
          <HeaderRow style={{ width: totalWidth }}>
            {headers.map((header, colIdx) => (
              <HeaderCell
                key={colIdx}
                style={{ width: colWidths[colIdx], minWidth: colWidths[colIdx] }}
              >
                <span>{header || `Column ${colIdx + 1}`}</span>
                <input
                  type="text"
                  placeholder="Filter..."
                  defaultValue=""
                  onChange={(e) => handleFilterChange(header, e.target.value)}
                  onClick={(e) => e.stopPropagation()}
                />
              </HeaderCell>
            ))}
          </HeaderRow>
        </HeaderScroll>

        {/* Virtualized body */}
        <BodyScroll ref={bodyRef} onScroll={handleBodyScroll}>
          <div style={{ position: "relative", height: totalHeight, width: totalWidth }}>
            {visibleRows}
          </div>
        </BodyScroll>
      </TableWrapper>
    </div>
  );
}